/**
 * ImageComparisonModal Component
 * Premium full-screen modal with side-by-side image comparison
 * No scrolling - responsive images fit within viewport
 */
import React from "react";
import { X, ImageIcon, Sparkles } from "lucide-react";

interface ImageComparisonModalProps {
  inputImage: string;
  outputImage: string;
  jobNumber?: string;
  onClose: () => void;
}

export const ImageComparisonModal: React.FC<ImageComparisonModalProps> = ({
  inputImage,
  outputImage,
  jobNumber,
  onClose,
}) => {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div 
        className="bg-slate-900 border border-slate-700/50 rounded-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex justify-between items-center px-6 py-4 border-b border-slate-700/50 bg-slate-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Image Comparison
              </h3>
              {jobNumber && (
                <p className="text-xs text-slate-400 font-mono">#{jobNumber.slice(-20)}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Image Container - Flex grow to fill remaining space */}
        <div className="flex-1 grid grid-cols-2 gap-4 p-4 min-h-0">
          {/* Input Image */}
          <div className="flex flex-col min-h-0">
            <div className="flex-shrink-0 text-center mb-3">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-blue-500/10 text-blue-400 border border-blue-500/30">
                <ImageIcon className="w-4 h-4" />
                Input Image
              </span>
            </div>
            <div className="flex-1 bg-slate-800/50 rounded-xl border border-slate-700/30 flex items-center justify-center p-4 min-h-0 overflow-hidden">
              <img
                src={inputImage}
                alt="Input"
                className="max-w-full max-h-full object-contain rounded-lg"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder-image.png";
                }}
              />
            </div>
          </div>

          {/* Output Image */}
          <div className="flex flex-col min-h-0">
            <div className="flex-shrink-0 text-center mb-3">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                <Sparkles className="w-4 h-4" />
                Annotated Output
              </span>
            </div>
            <div className="flex-1 bg-slate-800/50 rounded-xl border border-slate-700/30 flex items-center justify-center p-4 min-h-0 overflow-hidden">
              <img
                src={outputImage}
                alt="Output"
                className="max-w-full max-h-full object-contain rounded-lg"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder-image.png";
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-700/50 bg-slate-800/30 flex justify-center">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-8 py-2.5 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-medium rounded-xl transition-all shadow-lg shadow-primary-500/20"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
